import React, { useEffect, useState } from 'react';
import { axios } from '../../libs/axios';
import { useTranslation } from '../../i18n/I18nContext';
import { fmtScore, fmtPct, fmtNum } from './charts';
import { LawyerResultsTable } from './LawyerResultsTable';
import { MatchV2CardPreview } from './MatchV2CardPreview';
import { formatCostUsd } from './classifierCostPure';
import { buildClassifyKey } from './classifyKey';
import {
  getClassifierModelCapabilities,
  defaultThinkingEffortForModel,
} from './classifierModelCapabilities';

function formatClassifyDurationMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatClassifierCostLine(data, t) {
  const model = data?.classifierModel || data?.model;
  if (!model) return null;
  const provider = data.classifierProvider || data.provider || 'openai';
  const est = data.classifierEstimatedCost || data.estimatedCost;
  const actual = data.classifierActualCost || data.actualCost;
  const parts = [
    t('match.classifierRunModel', { provider, model }),
  ];
  if (est) {
    parts.push(t('match.classifierCostEstimated', { cost: formatCostUsd(est.costUsd) }));
  }
  if (actual) {
    parts.push(t('match.classifierCostActual', {
      cost: formatCostUsd(actual.costUsd),
      input: actual.inputTokens ?? '—',
      output: actual.outputTokens ?? '—',
    }));
  }
  if (data?.durationMs != null) {
    parts.push(t('match.classifyDuration', { seconds: formatClassifyDurationMs(data.durationMs) }));
  }
  return parts.join(' | ');
}

function renderSlotCell(row, t, eTop5 = 1.0) {
  const expert = row.E != null && row.E >= eTop5 - 0.001;
  const parts = [];
  if (row.selected) {
    if (row.reservedSlot) {
      parts.push(
        <span key="slot" className="match-slot-badge match-slot-badge--kvoot">{t('match.slot.kvoot')}</span>,
      );
    } else {
      parts.push(
        <span key="slot" className="match-slot-badge match-slot-badge--teene">{t('match.slot.teene')}</span>,
      );
    }
  }
  if (expert) {
    parts.push(
      <span
        key="expert"
        className="match-slot-expert"
        title={t('match.slot.expert')}
        aria-label={t('match.slot.expert')}
      >
        ★
      </span>,
    );
  }
  if (parts.length === 0) return '';
  return <span className="match-slot-cell">{parts}</span>;
}

function renderQuotaResolution(selection, rows, t) {
  const outcome = selection?.reservedSlotOutcome || 'not_applicable';
  let message;
  if (outcome === 'filled') {
    const name = selection.reservedLawyerName
      || rows.find((r) => r.user_no === selection.reservedSlotFilledBy)?.name
      || selection.reservedSlotFilledBy;
    const maturity = selection.reservedLawyerMaturity
      ?? rows.find((r) => r.user_no === selection.reservedSlotFilledBy)?.maturity
      ?? 0;
    message = t('match.quota.filled', { name, maturity: fmtScore(maturity) });
  } else if (outcome === 'none_eligible') {
    message = t('match.quota.noneEligible');
  } else if (outcome === 'all_eligible_already_selected') {
    message = t('match.quota.allEligibleAlreadySelected');
  } else if (outcome === 'no_below_threshold') {
    message = t('match.quota.noneEligible');
  } else if (outcome === 'all_new_already_selected') {
    message = t('match.quota.allEligibleAlreadySelected');
  } else {
    message = t('match.quota.notApplicable');
  }
  return <div className="match-quota-resolution">{message}</div>;
}

function formatCoverageTitle(row, t) {
  const weights = (row.fieldRankWeightsEffective || []).join(', ');
  if (!weights) return undefined;
  const padNote = row.fieldRankWeightsDidPad ? ` (${t('match.coverageWeightsPadded')})` : '';
  return t('match.coverageWeightsTooltip', { weights: `[${weights}]${padNote}` });
}

function formatMaturityTitle(row) {
  const terms = row.maturityTerms;
  if (!terms) return undefined;
  return `done ${fmtScore(terms.done)} · rating ${fmtScore(terms.rating)} · recent ${fmtScore(terms.recent)}`;
}

export function SimulatorTab({
  paramsProfile,
  onProfileChange,
  inquiryText = '',
  onInquiryTextChange,
  classifierCache,
  onClassifierCacheChange,
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(inquiryText);
  const [overrideFields, setOverrideFields] = useState(['']);
  const [lang, setLang] = useState('et');
  const [clientType, setClientType] = useState('person');
  const [compareProfile, setCompareProfile] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [profileParams, setProfileParams] = useState({});
  const [classifierResults, setClassifierResults] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [droppedFields, setDroppedFields] = useState([]);
  const [legalFields, setLegalFields] = useState([]);
  const [result, setResult] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [classifyMeta, setClassifyMeta] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [classifyStartedAt, setClassifyStartedAt] = useState(null);
  const [classifyElapsedMs, setClassifyElapsedMs] = useState(null);
  const [savedSimulations, setSavedSimulations] = useState([]);
  const [loadedSimulation, setLoadedSimulation] = useState(null);
  const [previewSeed, setPreviewSeed] = useState('sim-preview');

  useEffect(() => {
    setText(inquiryText);
  }, [inquiryText]);

  useEffect(() => {
    (async () => {
      const [p, f, sims] = await Promise.all([
        axios.get('/match/params'),
        axios.get('/legal-fields/active'),
        axios.get('/match/simulations'),
      ]);
      setProfiles(p.profiles || []);
      setLegalFields(f.data || []);
      setSavedSimulations(sims.simulations || []);
      const active = (p.profiles || []).find((pr) => pr.name === paramsProfile);
      setProfileParams(active?.params || {});
    })();
  }, [paramsProfile]);

  useEffect(() => {
    if (!classifyStartedAt) return undefined;
    const tick = () => setClassifyElapsedMs(Date.now() - classifyStartedAt);
    tick();
    const intervalId = setInterval(tick, 100);
    return () => clearInterval(intervalId);
  }, [classifyStartedAt]);

  const syncText = (value) => {
    setText(value);
    onInquiryTextChange?.(value);
    onClassifierCacheChange?.({ text: value, results: null, key: '' });
    setClassifyMeta(null);
    setClassifyStartedAt(null);
    setClassifyElapsedMs(null);
  };

  const activeOverrideIds = overrideFields
    .map((v) => Number(v))
    .filter((id) => Number.isFinite(id) && id > 0);

  const updateOverrideField = (idx, value) => {
    setOverrideFields((prev) => {
      const next = [...prev];
      next[idx] = value;
      return next;
    });
  };

  const addOverrideField = () => {
    if (overrideFields.length >= 3) return;
    setOverrideFields((prev) => [...prev, '']);
  };

  const removeOverrideField = (idx) => {
    setOverrideFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const classify = async () => {
    if (!text.trim()) return;
    const startedAt = Date.now();
    setClassifyStartedAt(startedAt);
    setClassifyElapsedMs(0);
    setClassifying(true);
    try {
      const data = await axios.post('/match/classify', { text, paramsProfile });
      if (data?.status >= 400) return;
      const durationMs = Date.now() - startedAt;
      setClassifierResults(data.results || []);
      setSelectedFields(data.selectedFields || []);
      setDroppedFields(data.droppedFields || []);
      const classifiedAt = new Date().toISOString();
      setClassifyMeta({
        source: 'fresh',
        classifiedAt,
        durationMs,
        classifierProvider: data.provider,
        classifierModel: data.model,
        classifierEstimatedCost: data.estimatedCost,
        classifierActualCost: data.actualCost,
        classifierUsage: data.usage,
        classifierJson: data.classifierJson || null,
      });
      const key = buildClassifyKey(text, profileParams);
      onClassifierCacheChange?.({
        text,
        results: data.results || [],
        key,
        classifiedAt,
      });
    } finally {
      setClassifying(false);
      setClassifyStartedAt(null);
    }
  };

  const applySimulateResponse = (data, reuse = false) => {
    setClassifierResults(data.classifierResults || []);
    setSelectedFields(data.selectedFields || []);
    setDroppedFields(data.droppedFields || []);
    if (data.classifierResults?.length && text.trim() && data.fieldSource !== 'override') {
      const key = buildClassifyKey(text, profileParams);
      const classifiedAt = classifyMeta?.classifiedAt || classifierCache?.classifiedAt || new Date().toISOString();
      onClassifierCacheChange?.({
        text,
        results: data.classifierResults,
        key,
        classifiedAt,
      });
      setClassifyMeta((prev) => ({
        ...(prev || {}),
        source: data.fieldSource === 'reuse' || reuse ? 'reused' : (prev?.source || 'fresh'),
        classifiedAt,
        classifierProvider: data.classifierProvider || prev?.classifierProvider,
        classifierModel: data.classifierModel || prev?.classifierModel,
        classifierEstimatedCost: data.classifierEstimatedCost || prev?.classifierEstimatedCost,
        classifierActualCost: data.classifierActualCost || prev?.classifierActualCost,
        classifierUsage: data.classifierUsage || prev?.classifierUsage,
        classifierJson: data.classifierJson || prev?.classifierJson,
      }));
    }
    if (data.fieldSource === 'override') {
      setClassifyMeta({ source: 'override' });
    }
  };

  const simulate = async (save = false) => {
    setLoading(true);
    try {
      const hasOverride = activeOverrideIds.length > 0;
      const cacheKey = buildClassifyKey(text, profileParams);
      const canReuseClassifier = !hasOverride
        && classifierCache?.key === cacheKey
        && classifierCache?.results?.length;

      const body = {
        text: hasOverride ? undefined : text,
        field_ids: hasOverride ? activeOverrideIds : undefined,
        classifierResults: canReuseClassifier ? classifierCache.results : undefined,
        lang,
        clientType,
        paramsProfile,
        save,
        previewSeed,
      };
      const data = await axios.post('/match/simulate', body);
      if (data?.status >= 400) return;
      applySimulateResponse(data, canReuseClassifier);
      setResult(data);
      if (save && data.saved) {
        window.nSuccess?.(t('match.simulationSaved'));
        const sims = await axios.get('/match/simulations');
        setSavedSimulations(sims.simulations || []);
      }

      if (compareProfile) {
        const cmp = await axios.post('/match/simulate', { ...body, paramsProfile: compareProfile, save: false });
        if (!cmp?.status || cmp.status < 400) {
          setCompareResult(cmp);
        }
      } else {
        setCompareResult(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSimulation = async (id) => {
    setLoading(true);
    try {
      const data = await axios.get(`/match/simulations/${id}`);
      if (data?.status >= 400) return;
      const sim = data.simulation;
      setLoadedSimulation(sim);
      setResult({
        fieldId: sim.fieldId,
        fieldIds: sim.fieldIds,
        classifierResults: sim.classifierResults,
        classifierModel: sim.classifierModel,
        classifierProvider: sim.classifierProvider,
        params: sim.params,
        rows: sim.resultRows,
        selection: sim.selection,
        fieldSource: 'saved',
      });
    } finally {
      setLoading(false);
    }
  };

  const extraColumns = [
    { key: 'E', label: t('match.col.E'), fmtType: 'score' },
    { key: 'R', label: t('match.col.R'), fmtType: 'score' },
    { key: 'S', label: t('match.col.S'), fmtType: 'score' },
    {
      key: 'respMedianH',
      label: t('match.col.respMedianH'),
      fmt: (v, row) => ((row.k ?? 0) > 0 && v != null ? `${fmtNum(v, 1)} h` : '—'),
      title: (row) => {
        const unit = profileParams.toggleBusinessHoursSpeed !== false
          ? t('match.respMedianH.unitBusiness')
          : t('match.respMedianH.unitCalendar');
        return t('match.colTooltip.respMedianH', {
          unit,
          tStar: row.tStar != null ? fmtNum(row.tStar, 1) : '—',
          k: row.k ?? 0,
        });
      },
    },
    { key: 'Q', label: t('match.col.Q'), fmtType: 'score' },
    { key: 'A', label: t('match.col.A'), fmtType: 'score' },
    { key: 'D', label: t('match.col.D'), fmtType: 'score' },
    {
      key: 'coverageMultiplier',
      label: t('match.col.coverage'),
      fmtType: 'score',
      title: (row) => formatCoverageTitle(row, t),
      fmt: (v, row) => {
        const c = row.C != null ? fmtScore(row.C) : '—';
        return `${c} · ${fmtScore(v ?? 1)}`;
      },
    },
    { key: 'sigma', label: t('match.col.sigma'), fmtType: 'score' },
    {
      key: 'badgeHints',
      label: t('match.col.badgeHints'),
      sortable: false,
      fmt: (_v, row, tr, badgeHints) => (badgeHints?.[row.user_no] || []).join(' · ') || '—',
    },
    { key: 'softmaxP', label: t('match.softmaxP'), fmt: (v) => fmtPct(v) },
    { key: 'inclusionP', label: t('match.inclusionP'), fmt: (v) => fmtPct(v) },
    {
      key: 'maturity',
      label: t('match.col.maturity'),
      fmtType: 'score',
      title: (row) => formatMaturityTitle(row),
      fmt: (v, row) => {
        const base = fmtScore(v);
        if (row.quotaEligible) {
          return (
            <span>
              {base}
              {' '}
              <span className="match-slot-badge match-slot-badge--kvoot" title={row.quotaReason || ''}>
                {t('match.quotaEligibleShort')}
              </span>
            </span>
          );
        }
        return base;
      },
    },
    {
      key: 'declaredSelectedCount',
      label: t('match.declaredFields'),
      fmt: (_v, row, tr) => {
        const count = row.declaredSelectedCount ?? 0;
        const ranks = (row.declaredSelectedRanks || []).join(',');
        if (count > 0 && ranks) {
          return tr('match.declaredFieldsFmt', { count, ranks });
        }
        return String(count);
      },
    },
  ];

  const renderClassificationStatus = () => {
    if (!classifyMeta && !result?.fieldSource) return null;
    const source = result?.fieldSource === 'override'
      ? 'override'
      : (classifyMeta?.source || result?.fieldSource || 'classifier');
    const label = t(`match.classificationSource.${source}`);
    const at = classifyMeta?.classifiedAt
      ? new Date(classifyMeta.classifiedAt).toLocaleString()
      : '';
    return (
      <p className="small text-muted mb-2">
        {t('match.classificationStatus', {
          source: label,
          model: classifyMeta?.classifierModel || result?.classifierModel || '—',
          at,
        })}
      </p>
    );
  };

  const renderResult = (data, label) => {
    if (!data) return null;
    const eTop5 = data.params?.eTop5 ?? 1.0;
    const resultColumns = [
      ...extraColumns,
      {
        key: 'slot',
        label: t('match.slot'),
        fmt: (_v, row) => renderSlotCell(row, t, eTop5),
      },
    ];
    const fieldSourceLabel = data.fieldSource === 'override'
      ? t('match.fieldSourceOverride')
      : data.fieldSource === 'reuse'
        ? t('match.fieldSourceReuse')
        : data.fieldSource === 'saved'
          ? t('match.fieldSourceSaved')
          : t('match.fieldSourceClassifier');
    const primaryName = (data.selectedFields || []).find((f) => f.field_id === data.fieldId)?.name
      || data.fieldId;
    return (
      <div className="mb-4">
        <h4>{label}</h4>
        {data.classifierWarning && (
          <div className="alert alert-warning py-1">
            {t('match.classifierWarning')}: {data.classifierWarning}
          </div>
        )}
        {data.nonDiscriminating?.length > 0 && (
          <div className="alert alert-warning py-1">
            {t('match.nonDiscriminating')}: {data.nonDiscriminating.join(', ')}
          </div>
        )}
        <p>
          {t('match.fieldUsed')}: {(data.fieldIds || [data.fieldId]).join(', ')} ({fieldSourceLabel})
          {' '}| {t('match.fieldUsedForE')}: {data.fieldId} ({primaryName})
          {' '}| {t('match.shown')}: {(data.selection?.shown || []).join(', ')}
        </p>
        {renderClassificationStatus()}
        {data.classifierModel && (
          <p className="small text-muted mb-1">
            {formatClassifierCostLine(data, t) || `Model: ${data.classifierModel}`}
          </p>
        )}
        {(data.resolvedPrompt || data.rawClassifierResponse) && (
          <div className="mb-2">
            {data.resolvedPrompt && (
              <div>
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowPrompt((v) => !v)}>
                  {t('match.resolvedPrompt')}
                </button>
                {showPrompt && (
                  <pre className="small bg-light p-2 mt-1" style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.resolvedPrompt}
                  </pre>
                )}
              </div>
            )}
            {data.rawClassifierResponse && (
              <div>
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowRaw((v) => !v)}>
                  {t('match.rawClassifierResponse')}
                </button>
                {showRaw && (
                  <pre className="small bg-light p-2 mt-1" style={{ maxHeight: 120, overflow: 'auto' }}>
                    {data.rawClassifierResponse}
                  </pre>
                )}
              </div>
            )}
          </div>
        )}
        <LawyerResultsTable
          rows={data.rows || []}
          extraColumns={resultColumns}
          paramsProfile={paramsProfile}
          badgeHints={data.badgeHints}
        />
        {data.clientDisplay?.matched?.length > 0 && (
          <div className="mt-3">
            <h5>{t('match.clientCardPreview')}</h5>
            <p className="small text-muted">{t('match.clientCardPreviewDesc')}</p>
            <MatchV2CardPreview cards={data.clientDisplay.matched} />
          </div>
        )}
        {renderQuotaResolution(data.selection, data.rows || [], t)}
        {data.excluded?.length > 0 && (
          <div className="mt-2">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setShowExcluded((v) => !v)}
            >
              {t('match.excludedFromSelection')} ({data.excluded.length})
            </button>
            {showExcluded && (
              <table className="table table-sm mt-2">
                <thead>
                  <tr><th>{t('match.colName')}</th><th>L</th><th>Σ</th><th>{t('match.reason')}</th></tr>
                </thead>
                <tbody>
                  {data.excluded.map((e) => (
                    <tr key={e.user_no}>
                      <td>{e.name || e.user_no}</td>
                      <td>{fmtScore(e.L)}</td>
                      <td>{fmtScore(e.sigma)}</td>
                      <td>{(e.reasons || []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="match-simulator">
      <div className="form-row col-full mb-3">
        <label>{t('match.inquiryText')}</label>
        <textarea
          className="form-control"
          rows={4}
          value={text}
          onChange={(e) => syncText(e.target.value)}
        />
        <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={classify}
            disabled={classifying || loading || !text.trim()}
          >
            {classifying ? t('common.loading') : t('match.classify')}
          </button>
          {classifying && classifyElapsedMs != null && (
            <span className="small text-muted" aria-live="polite">
              {t('match.classifyDuration', { seconds: formatClassifyDurationMs(classifyElapsedMs) })}
            </span>
          )}
        </div>
      </div>

      {classifierResults.length > 0 && (
        <div className="mb-3">
          <h5>{t('match.classifierResults')}</h5>
          {classifyMeta?.classifierModel && (
            <p className="small text-muted">
              {formatClassifierCostLine(classifyMeta, t)}
            </p>
          )}
          <ul>
            {classifierResults.map((r) => {
              const isSelected = selectedFields.some((s) => s.field_id === r.field_id);
              const dropped = droppedFields.find((d) => d.field_id === r.field_id);
              let suffix = '';
              if (isSelected) suffix = ' ✓';
              else if (dropped) suffix = ` (${t(`match.dropReason.${dropped.dropReason || 'count_limit'}`)})`;
              return (
                <li key={r.field_id}>
                  {r.field_id} — {r.name} ({fmtPct(r.confidence)}){suffix}
                </li>
              );
            })}
          </ul>
          {classifyMeta?.classifierJson && Object.keys(classifyMeta.classifierJson).length > 0 && (
            <div className="mt-2">
              <p className="small text-muted mb-1">{t('match.classifierJson')}</p>
              <pre className="small bg-light p-2 mb-0" style={{ maxHeight: 240, overflow: 'auto' }}>
                {JSON.stringify(classifyMeta.classifierJson, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-5">
          <label>{t('match.fieldOverrideMulti')}</label>
          {overrideFields.map((fieldVal, idx) => (
            <div key={idx} className="d-flex gap-2 mb-1">
              <select
                className="form-control"
                value={fieldVal}
                onChange={(e) => updateOverrideField(idx, e.target.value)}
              >
                <option value="">—</option>
                {(Array.isArray(legalFields) ? legalFields : []).map((f) => (
                  <option key={f.field_id} value={f.field_id}>
                    {f.field_id} — {f.name_et}
                  </option>
                ))}
              </select>
              {overrideFields.length > 1 && (
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => removeOverrideField(idx)}>
                  {t('match.removeField')}
                </button>
              )}
            </div>
          ))}
          {overrideFields.length < 3 && (
            <button type="button" className="btn btn-link btn-sm p-0" onClick={addOverrideField}>
              {t('match.addField')}
            </button>
          )}
        </div>
        <div className="col-md-2">
          <label>{t('match.language')}</label>
          <select className="form-control" value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="et">et</option>
            <option value="en">en</option>
            <option value="ru">ru</option>
          </select>
        </div>
        <div className="col-md-2">
          <label>{t('match.clientType')}</label>
          <select className="form-control" value={clientType} onChange={(e) => setClientType(e.target.value)}>
            <option value="person">{t('match.person')}</option>
            <option value="business">{t('match.business')}</option>
          </select>
        </div>
        <div className="col-md-3">
          <label>{t('match.activeProfile')}</label>
          <select
            className="form-control"
            value={paramsProfile}
            onChange={(e) => onProfileChange?.(e.target.value)}
          >
            {profiles.map((p) => (
              <option key={p.name} value={p.name}>
                {p.name}{p.isDefault ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label>{t('match.compareProfile')}</label>
          <select className="form-control" value={compareProfile} onChange={(e) => setCompareProfile(e.target.value)}>
            <option value="">—</option>
            {profiles.filter((p) => p.name !== paramsProfile).map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4">
          <label>{t('match.previewSeed')}</label>
          <input
            className="form-control form-control-sm"
            value={previewSeed}
            onChange={(e) => setPreviewSeed(e.target.value)}
          />
        </div>
      </div>

      <button type="button" className="btn btn-primary me-2" onClick={() => simulate(false)} disabled={loading}>
        {t('match.run')}
      </button>
      <button type="button" className="btn btn-outline-primary" onClick={() => simulate(true)} disabled={loading}>
        {t('match.runAndSave')}
      </button>

      {loading && <div className="mt-2">{t('common.loading')}</div>}

      {renderResult(result, t('match.results'))}
      {renderResult(compareResult, `${t('match.results')} (${compareProfile})`)}

      <div className="mt-4">
        <h5>{t('match.savedSimulations')}</h5>
        {savedSimulations.length === 0 ? (
          <p className="text-muted small">{t('match.noSavedSimulations')}</p>
        ) : (
          <ul className="list-unstyled small">
            {savedSimulations.map((sim) => (
              <li key={sim.id} className="mb-1">
                <button type="button" className="btn btn-link btn-sm p-0" onClick={() => loadSimulation(sim.id)}>
                  {new Date(sim.createdAt).toLocaleString()}
                  {' — '}
                  {(sim.inputTextPreview || '').slice(0, 60)}
                  {sim.paramsProfileName ? ` (${sim.paramsProfileName})` : ''}
                </button>
              </li>
            ))}
          </ul>
        )}
        {loadedSimulation && (
          <p className="small text-muted">{t('match.viewingSavedSimulation')}</p>
        )}
      </div>
    </div>
  );
}
